import { Injectable, TemplateRef } from '@angular/core';
import { mergeMap, map } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ErrorMessageComponent } from 'src/app/shared/presentationals/messages/error-message/error-message.component';
import { NotificationMessageComponent } from 'src/app/shared/presentationals/messages/notification-message/notification-message.component';
import { SuccessMessageComponent } from 'src/app/shared/presentationals/messages/success-message/success-message.component';
import { DirtyComponent } from '../components/dirty.component';
import { ConfirmMessageComponent } from 'src/app/shared/presentationals/messages/confirm-message/confirm-message.component';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { BaseComponent } from '../components/base.component';
import { MatDialog } from '@angular/material/dialog';
@Injectable({
  providedIn: 'root'
})
export class MessageService {
  constructor(private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
    private readonly dialog: MatDialog) {
  }

  public openErrorRaw(message: Observable<string>): MatSnackBarRef<ErrorMessageComponent> {
    return this.snackBar.openFromComponent(ErrorMessageComponent, {
      horizontalPosition: 'end',
      data: message
    });
  }

  public openError(message: string): MatSnackBarRef<ErrorMessageComponent> {
    return this.openErrorRaw(of(message));
  }

  public translateOpenError(key: string, params?: object): MatSnackBarRef<ErrorMessageComponent> {
    return this.openErrorRaw(this.translateService.get(key, params));
  }

  public openNotificationRaw(message: Observable<string>): MatSnackBarRef<NotificationMessageComponent> {
    return this.snackBar.openFromComponent(NotificationMessageComponent, {
      horizontalPosition: 'end',
      data: message
    });
  }

  public openNotification(message: string): MatSnackBarRef<NotificationMessageComponent> {
    return this.openNotificationRaw(of(message));
  }

  public translateOpenNotification(key: string, params?: object): MatSnackBarRef<NotificationMessageComponent> {
    return this.openNotificationRaw(this.translateService.get(key, params));
  }

  private openSuccessRaw(message: Observable<string>, duration: number = 3000): MatSnackBarRef<SuccessMessageComponent> {
    return this.snackBar.openFromComponent(SuccessMessageComponent, {
      horizontalPosition: 'end',
      data: message,
      duration: duration
    });
  }

  public openSuccess(message: string, duration: number = 3000): MatSnackBarRef<SuccessMessageComponent> {
    return this.openSuccessRaw(of(message), duration);
  }

  public translateOpenSuccess(key: string, interpolateParams?: object, duration?: number): MatSnackBarRef<SuccessMessageComponent> {
    return this.openSuccessRaw(this.translateService.get(key, interpolateParams), duration);
  }

  public openSaveSuccess(): MatSnackBarRef<SuccessMessageComponent> {
    return this.translateOpenSuccess('Messages.SaveSuccess');
  }

  public openSpecialSaveSuccess(): MatSnackBarRef<SuccessMessageComponent> {
    return this.snackBar.openFromComponent(SuccessMessageComponent, {
      horizontalPosition: 'end',
      data: this.translateService.get('Messages.SaveSuccess'),
      duration: 5000,
      panelClass: 'green-snackbar'
    });
  }

  public openDeleteSuccess(): MatSnackBarRef<SuccessMessageComponent> {
    return this.translateOpenSuccess('Messages.DeleteSuccess');
  }

  public openUnexpectedError(): MatSnackBarRef<ErrorMessageComponent> {
    return this.translateOpenError('Messages.UnexpectedError');
  }

  private openConfirmRaw(component: BaseComponent,
    message: Observable<string>,
    primaryButtonLabel: Observable<string>,
    secondaryButtonLabel: Observable<string>,
    next?: (isConfirmed: boolean) => void): MatSnackBarRef<ConfirmMessageComponent> {
    const snackBarRef = this.snackBar.openFromComponent(ConfirmMessageComponent, {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'show-large-text',
      data: {
        message: message,
        primaryButtonLabel: primaryButtonLabel,
        secondaryButtonLabel: secondaryButtonLabel,
      }
    });
    if (next) {
      snackBarRef.afterDismissed()
        .pipe(map(() => snackBarRef.instance.isConfirmed))
        .unsubscribeOnDestroy(component)
        .subscribe(isConfirmed => {
          next(isConfirmed);
        });
    }

    return snackBarRef;
  }

  public openConfirm(component: BaseComponent,
    message: string,
    primaryButtonLabel: string,
    secondaryButtonLabel: string,
    next?: (isConfirmed: boolean) => void): MatSnackBarRef<ConfirmMessageComponent> {
    return this.openConfirmRaw(component, of(message), of(primaryButtonLabel), of(secondaryButtonLabel), next);
  }

  public translateOpenConfirm(component: BaseComponent, messageKey: string, messageInterpolateParams?: object, primaryButtonLabelKey: string = 'Common.Ok', secondaryButtonLabelKey: string = 'Common.Cancel', next?: (isConfirmed: boolean) => void): MatSnackBarRef<ConfirmMessageComponent> {
    return this.openConfirmRaw(component,
      this.translateService.get(messageKey, messageInterpolateParams),
      this.translateService.get(primaryButtonLabelKey),
      this.translateService.get(secondaryButtonLabelKey),
      next);
  }

  public openDeleteConfirm(component: BaseComponent, onConfirm: () => void) {
    this.translateOpenConfirm(component,
      'Messages.DeleteConfirmation',
      undefined,
      'Common.Delete',
      'Common.Cancel',
      (isConfirmed: boolean) => {
        if (isConfirmed) {
          onConfirm();
        }
      });
  }

  public openDirtyConfirm(component: DirtyComponent & BaseComponent): Observable<boolean> {
    return component.isDirty.pipe(
      mergeMap(isDirty => {
        if (isDirty) {
          const snackBarRef = this.translateOpenConfirm(component, 'Messages.CancelDirtyConfirmation');

          return snackBarRef
            .afterDismissed()
            .unsubscribeOnDestroy(component)
            .pipe(map(() => {
              return snackBarRef.instance.isConfirmed;
            }));
        }

        return of(true);
      }));
  }

  public dimissOpenedSnackbars(withDurationOnly?: boolean): void {
    if (this.snackBar._openedSnackBarRef) {
      if (withDurationOnly && this.snackBar._openedSnackBarRef?.containerInstance.snackBarConfig.duration === 0) {
        return;
      }

      this.snackBar.dismiss();
    }
  }
}
